import type { FC } from 'react';
import { Stack, useTheme } from '@mui/material';
import { Iconify, Label, Text } from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import type { KYCDocument } from '@pflegenavi/service-provider-frontend/api';

interface KycDocumentViewProps {
  name: string;
  isVerified: boolean;
  status?: KYCDocument['Status'];
  onEdit?: () => void;
}

export const KycDocumentView: FC<KycDocumentViewProps> = ({
  name,
  isVerified,
  status,
  onEdit,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const color =
    status === 'VALIDATED'
      ? 'success'
      : status === 'VALIDATION_ASKED'
      ? 'warning'
      : ['REFUSED', 'OUT_OF_DATE'].includes(status ?? '')
      ? 'error'
      : 'default';

  const label =
    status === 'VALIDATED'
      ? t('kyc.identity.status.success')
      : status === 'VALIDATION_ASKED'
      ? t('kyc.identity-verification-pending')
      : ['REFUSED', 'OUT_OF_DATE'].includes(status ?? '')
      ? t('kyc.identity-verification-failed')
      : t('kyc.identity-not-verified');

  const editable =
    !isVerified &&
    (!status || ['CREATED', 'REFUSED', 'OUT_OF_DATE'].includes(status ?? ''));

  return (
    <Stack
      data-testid="kyc-document-section"
      direction="row"
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 3,
        bgcolor: theme.palette.background.neutral,
        borderRadius: theme.shape.borderRadius,
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Text
          data-testid="kyc-document-name"
          style={{
            fontSize: 17,
            fontWeight: 700,
          }}
        >
          {name}
        </Text>
        {editable && onEdit && (
          <Iconify
            data-testid="kyc-document-edit-button"
            icon={'eva:edit-fill'}
            sx={{
              cursor: 'pointer',
              color: 'primary.main',
            }}
            onClick={onEdit}
          />
        )}
      </Stack>
      <Label data-testid="kyc-document-status" color={color}>
        {label}
      </Label>
    </Stack>
  );
};
